import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import { mockData } from "Payments/PaymentPage/mockData";
import qs from "qs";

import {
  SharedResizingChild,
  SharedResizingContainer,
} from "UI/SharedResizingArea";

import { usePaymentPage } from "../PaymentPageContext";

import AddOnGiftsSection from "./AddOnGiftsSection";
import AmountSection from "./AmountSection";
import ApplyPaymentToExistingPledgeSection from "./ApplyPaymentToPledgeSection";
import AuthSection from "./AuthSection";
import { useSyncClientAuthState } from "./AuthSection/stateHooks";
import FundHeader, { FundDescription } from "./IntroSection";
import PaymentScreenMemo from "./MemoSection/PaymentScreenMemo";
import FooterLogo from "./PaymentDrawerElements/FooterLogo";
import { PaymentFormProvider, usePaymentForm } from "./PaymentFormContext";
import PaymentMethodSection from "./PaymentMethodSection";
import SubmitSection from "./SubmitSection";
import { pageWidth } from "./styles.js";
import { useHandleAmountQueryString } from "./useHandleAmountQueryString";
import { useSetInitialFormStages } from "./useSetInitialFormStages";

function PaymentFormScreen({
  triggerSetFormStage,
  resetTrigger,
  syncClientAuthStateRef,
}) {
  syncClientAuthStateRef.current = useSyncClientAuthState();
  const { state, dispatch } = usePaymentPage();
  const {
    state: { formStages },
    dispatch: formDispatch,
  } = usePaymentForm();
  const location = useLocation();
  const {
    amount: amountURLParam,
    pledgeID,
    recurringStartDate,
    recurringEndDate,
    recurringSchedule,
    emailPhone,
  } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  useSetInitialFormStages();
  useHandleAmountQueryString(amountURLParam);

  const getInitialIntroIsComplete = () => {
    const { noteInfo, modifyPayment, fund, campaign } = mockData.shouldMock
      ? mockData.paymentPageData
      : window.data?.paymentPageData;
    const cameFromNote = noteInfo?.noteID;
    const isModifyFlow = modifyPayment?.paymentID;
    const hasNoFundDetails = !fund?.details;

    const { hasRecurringOn, suggestedRecurring } = window.data.paymentPageData;
    const { startDate, endDate, interval } = suggestedRecurring || {};
    const isForPledge =
      (campaign?.pledge && hasRecurringOn) /* logged in user */ ||
      (amountURLParam &&
        pledgeID &&
        recurringEndDate &&
        recurringStartDate &&
        recurringStartDate &&
        recurringSchedule &&
        emailPhone); /* logged out user */

    return (
      amountURLParam ||
      cameFromNote ||
      isModifyFlow ||
      hasNoFundDetails ||
      (hasRecurringOn && (startDate || endDate || interval)) ||
      isForPledge ||
      state?.paymentParent?.hideIntroScreen ||
      mockData.skipIntroScreen
    );
  };

  const [introIsComplete, setIntroIsComplete] = useState(
    getInitialIntroIsComplete
  );

  useEffect(() => {
    dispatch({
      type: "setHeaderIsBig",
      payload: !introIsComplete,
    });
  }, [introIsComplete, dispatch]);

  useEffect(() => {
    if (triggerSetFormStage) {
      formDispatch({
        type: "setFormStage",
        stage: "paymentInfo",
        value: "methodSelected",
      });
      resetTrigger();
    }
  }, [triggerSetFormStage, formDispatch, resetTrigger]);

  return (
    <Wrapper>
      <FundHeader introIsComplete={introIsComplete} />
      <SharedResizingContainer bounce={true}>
        {introIsComplete ? (
          <SharedResizingChild key="payment">
            <PaymentSections>
              <PaymentSectionsInside>
                <AmountSection amountURLParam={amountURLParam} />
                <AuthSection />
                <PaymentMethodSection />
                <AddOnGiftsSection />
                <PaymentScreenMemo />
                <ApplyPaymentToExistingPledgeSection />
                <SubmitSection />
                <FooterLogo />
              </PaymentSectionsInside>
            </PaymentSections>
          </SharedResizingChild>
        ) : (
          <SharedResizingChild key="descrip">
            <FundDescription setIntroIsComplete={setIntroIsComplete} />
          </SharedResizingChild>
        )}
      </SharedResizingContainer>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding: 20px 0;
`;

const PaymentSections = styled.div``;
const PaymentSectionsInside = styled.div`
  ${pageWidth}
`;

export default function PaymentFormScreenWithProvider(props) {
  return (
    <PaymentFormProvider>
      <PaymentFormScreen {...props} />
    </PaymentFormProvider>
  );
}
